import { Suspense, UIEvent, useEffect, useState } from 'react';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { FilterService } from 'primereact/api';
import { Skill, SkillResult } from '../../models/Skill';
import SingleUnitSkillDialog from '../SingleUnit/SingleUnitSkillDialog';
import { ProgressBar } from '../../models/ProgressBar';
import { progressBarDashboardSkillTab } from '../../businessLogicService/progressBarAssignValues';
import SingleUnitProgressBarSkillProgressTable from '../SingleUnit/SingleUnitProgressBarSkillProgressTable';
import QuerySkillsProgressReport, { ProgressTableSkillsValue } from './QuerySkillsProgressReport';
import QuerySkillsProgressReportStudents from './QuerySkillsProgressReportStudents';
import { InputText } from 'primereact/inputtext';
import { useLazyLoadQuery } from 'react-relay';
import { SkillsAllTargetSkillTypesQuery } from '../../graphQLOperations/__generated__/SkillsAllTargetSkillTypesQuery.graphql';
import { getAllTargetSkillTypes } from '../../graphQLOperations/Skills';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { useDebounce } from 'primereact/hooks';
import { PageInfo } from '../../models/PageInfo';
import { useAppSelector } from '../../redux/hooks';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { ProgressSkillCsvButton } from './ProgressSkillCsvButton';
import { selectedCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { PageControls } from '../../models/PageControls';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';

FilterService.register('custom_skillType', (value: string, filters: string[]) => {
  const array = value?.split(', ');
  if (filters?.length === 0) return true;
  return array.some(x => filters?.includes(x));
});

interface Filters {
  name: string;
  skillType: { id: string; name: string; isActive: boolean } | null;
  targetGroupIds: string[] | null;
  skillGradeIds: string[] | null;
  [key: string]: string | string[] | { id: string; name: string } | null;
}

export interface ProgressTableClassFilterState {
  filters: Filters;
}

const filterStateProgressSkillInitValue: ProgressTableClassFilterState = {
  filters: {
    name: '',
    skillType: null,
    targetGroupIds: null,
    skillGradeIds: null,
  },
};

const initialPageControls: PageControls = {
  first: 0,
  rows: 10,
  page: 0,
  pageCount: 0,
  prevPage: 0,
  totalPages: 0,
};

const ProgressTableClass = () => {
  const [filterState, setFilterState] = useState<ProgressTableClassFilterState>(filterStateProgressSkillInitValue);
  const [values, setValues] = useState<ProgressTableSkillsValue[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [skillName, debouncedSkillName, setDebouncedSkillName] = useDebounce<string | null>(null, 500);
  const reduxMonitoringFilters = useAppSelector(selectedReportsFilter);
  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  const [pageControls, setPageControls] = useState(initialPageControls);

  useEffect(() => {
    setFetchingData(true);
    setFilterState({
      ...filterState,
      filters: { ...filterState.filters, name: debouncedSkillName || '' },
    });
  }, [debouncedSkillName]);

  useEffect(() => {
    setFetchingData(true);
    setFilterState({
      filters: {
        ...filterState.filters,
        targetGroupIds: [reduxMonitoringFilters.roadmaps.id],
        skillGradeIds: reduxMonitoringFilters.skillGrades.map(sg => sg.code),
      },
    });
  }, [reduxMonitoringFilters]);

  useEffect(() => {
    setPageControls(initialPageControls);
  }, [filterState.filters]);

  const { allTargetSkillTypes } = useLazyLoadQuery<SkillsAllTargetSkillTypesQuery>(
    getAllTargetSkillTypes,
    {},
    { fetchPolicy: 'network-only' },
  );

  const [students, setStudents] = useState<
    {
      id: string;
      firstName: string;
      lastName: string;
      publicUserId: string;
    }[]
  >([]);
  const [lazyLoading, setLazyLoading] = useState<boolean>(true);
  const [lastCall, setLastCall] = useState<boolean>(false);
  const [fetchKeyScroll, setFetchKeyScroll] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [studentSkillProgressDetailInput, setStudentSkillProgressDetailInput] = useState<{
    skillId: string;
    skillResult: SkillResult;
    count: number;
  }>();

  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<Skill>();

  const resetFilterState = () => {
    setFilterState(filterStateProgressSkillInitValue);
  };

  const onScroll = (event: UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (!lastCall && scrollTop + clientHeight >= scrollHeight) {
      setLazyLoading(true);
      setFetchKeyScroll(prev => prev + 1);
    }
  };

  const skillNameTemplate = (skill: Skill) => {
    return (
      <>
        <a>
          <p
            key={skill.code}
            onClick={() => {
              setSkill(skill);
              setVisible(true);
            }}
          >
            {skill.skillName + ' (' + skill.code + ')'}
          </p>
        </a>
      </>
    );
  };

  const rowFilterTemplate = (option: ColumnFilterElementTemplateOptions, options: { id: string; name: string }[]) => (
    <Dropdown
      disabled={!options.length || isCsvProcessing}
      name={option.field}
      optionLabel="name"
      value={(filterState?.filters as Filters)[option.field]}
      options={options}
      itemTemplate={option => {
        return <span key={option.id}>{option?.name}</span>;
      }}
      onChange={(e: MultiSelectChangeEvent) => {
        setFetchingData(true);
        setFilterState({
          ...filterState,
          filters: {
            ...filterState.filters,
            [e.target.name]: e.target.value,
          },
        });
      }}
      placeholder="Search"
      className="p-column-filter"
      style={{ minWidth: '14rem' }}
      showClear
    />
  );

  return (
    <>
      <Suspense>
        <QuerySkillsProgressReport
          {...{
            setValues,
            fetchingData,
            filterState,
            pageControls,
            firstLoad,
            setFetchingData,
            setFirstLoad,
            setTotalRecords,
          }}
          resetFilterState={resetFilterState}
        />
      </Suspense>
      <Suspense>
        <QuerySkillsProgressReportStudents
          {...{
            fetchKeyScroll,
            lazyLoading,
            pageInfo,
            setLastCall,
            setLazyLoading,
            setPageInfo,
            setStudents,
            studentSkillProgressDetailInput,
          }}
        />
      </Suspense>
      <>
        <div className="w-full flex justify-content-end pb-3">
          <ProgressSkillCsvButton filters={filterState} totalRecords={totalRecords} />
        </div>
        {fetchingData ? (
          <ThreeDotLoader dark />
        ) : (
          <DataTable
            lazy
            sortField="skillName"
            sortOrder={+1}
            stripedRows
            paginator
            totalRecords={totalRecords}
            rows={pageControls.rows}
            rowsPerPageOptions={[5, 10, 25]}
            first={pageControls.first}
            filterDisplay="row"
            value={values}
            paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            onPage={(e: DataTableStateEvent) => {
              setFetchingData(true);
              const newPageControls: PageControls = {
                ...pageControls,
                page: e.page,
                rows: e.rows,
                first: e.first,
                pageCount: e.pageCount!,
                prevPage: pageControls.page!,
              };
              setPageControls(newPageControls);
            }}
          >
            <Column
              filter
              showFilterMenu={false}
              filterElement={() => (
                <InputText
                  disabled={isCsvProcessing}
                  value={skillName ? skillName : ''}
                  onChange={e => {
                    setDebouncedSkillName(e.target.value);
                  }}
                  placeholder="Search by name"
                />
              )}
              filterPlaceholder="Search by name"
              field="skillName"
              header="Skill Name"
              body={skillNameTemplate}
            />
            <Column
              field="skillType"
              header="Skill Type"
              filter
              showFilterMenu={false}
              filterElement={option =>
                rowFilterTemplate(option, allTargetSkillTypes as { id: string; name: string; isActive: boolean }[])
              }
            />
            <Column
              field="skillsResults"
              className="nc_report-progress-bar"
              body={(data: ProgressTableSkillsValue) => {
                const valuesProgress: ProgressBar[] = progressBarDashboardSkillTab(data?.skillsResults, data?.skillId);
                if (data?.skillsResults?.every(skill => skill.value === 0)) return null;
                return (
                  <SingleUnitProgressBarSkillProgressTable
                    values={valuesProgress}
                    setStudentSkillProgressDetailInput={setStudentSkillProgressDetailInput}
                    list={students}
                    onScroll={onScroll}
                    lazyLoading={lazyLoading}
                    setStudents={setStudents}
                    setLazyLoading={setLazyLoading}
                    studentSkillProgressDetailInput={studentSkillProgressDetailInput}
                    setPageInfo={setPageInfo}
                  />
                );
              }}
            />
          </DataTable>
        )}
      </>

      {visible && skill && <SingleUnitSkillDialog visible={visible} setVisible={setVisible} skill={skill} />}
    </>
  );
};

export default ProgressTableClass;
